import { Paper, Typography, TextField, Button, IconButton, Box } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import type { PRLink } from '../../../types/salesforce'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

export interface LinksSectionProps {
  prSheetId?: string
  links: PRLink[]
  onChange: (links: PRLink[]) => void
  onBlur?: () => void
}

function SortableLinkItem({
  link,
  index,
  onDelete,
  onChange,
  onBlur,
}: {
  link: PRLink
  index: number
  onDelete: () => void
  onChange: (field: keyof PRLink, value: string) => void
  onBlur?: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: link.Id! })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <Box ref={setNodeRef} style={style} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" {...attributes} {...listeners} sx={{ cursor: 'grab' }}>
            <DragIndicatorIcon />
          </IconButton>
          <Typography variant="subtitle2">リンク #{index + 1}</Typography>
        </Box>
        <IconButton onClick={onDelete} size="small" color="error">
          <DeleteIcon />
        </IconButton>
      </Box>

      <TextField
        label="名前"
        fullWidth
        margin="normal"
        value={link.LinkName__c || ''}
        onChange={(e) => onChange('LinkName__c', e.target.value)}
        onBlur={onBlur}
        placeholder="例: GitHubプロフィール, ポートフォリオサイト"
      />

      <TextField
        label="URL"
        fullWidth
        margin="normal"
        value={link.URL__c || ''}
        onChange={(e) => onChange('URL__c', e.target.value)}
        onBlur={onBlur}
        placeholder="https://..."
      />
    </Box>
  )
}

export function LinksSection({ prSheetId, links, onChange, onBlur }: LinksSectionProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )
  const handleAdd = () => {
    const newLink: PRLink = {
      Id: `temp-${Date.now()}`,
      PRSheet__c: prSheetId,
      LinkName__c: '',
      URL__c: '',
      SortOrder__c: (links.length + 1) * 10,
    }
    onChange([...links, newLink])
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = links.findIndex((link) => link.Id === active.id)
      const newIndex = links.findIndex((link) => link.Id === over.id)

      const reorderedLinks = arrayMove(links, oldIndex, newIndex)

      // SortOrder__cを更新
      const updatedLinks = reorderedLinks.map((link, index) => ({
        ...link,
        SortOrder__c: (index + 1) * 10,
      }))

      onChange(updatedLinks)
      if (onBlur) onBlur()
    }
  }

  const handleChange = (index: number, field: keyof PRLink, value: string) => {
    const newLinks = [...links]
    newLinks[index] = {
      ...newLinks[index],
      [field]: value,
    }
    onChange(newLinks)
  }

  const handleDelete = (index: number) => {
    const newLinks = links.filter((_, i) => i !== index)
    onChange(newLinks)
  }

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">🔗 外部リンク</Typography>
        <Button startIcon={<AddIcon />} onClick={handleAdd} variant="outlined" size="small">
          追加
        </Button>
      </Box>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={links.map((link) => link.Id!)} strategy={verticalListSortingStrategy}>
          {links.map((link, index) => (
            <SortableLinkItem
              key={link.Id}
              link={link}
              index={index}
              onDelete={() => handleDelete(index)}
              onChange={(field, value) => handleChange(index, field, value)}
              onBlur={onBlur}
            />
          ))}
        </SortableContext>
      </DndContext>

      {links.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
          外部リンクを追加してください
        </Typography>
      )}
    </Paper>
  )
}
